import alt from 'components/Dispatcher';
import LibraryActions from 'actions/library/LibraryActions';
import LibraryCreateActions from 'actions/library/LibraryCreateActions';
import immutableStore from 'alt-utils/lib/ImmutableUtil';
import {Record, Set} from 'immutable';
import LibrarySource from 'sources/library/LibrarySource';

const State = Record({
  id: null,
  name: '',
  purpose: '',
  shareType: null,
  shareId: null,
  shares: [],
  tags: [],
  currentTag: null,
  initialState: null,
  hasUnsavedShares: false,
});

@immutableStore
class LibraryCreateFormStore {
  constructor() {
    this.formInitialized = false;
    this.state = State();
    this.bindActions(LibraryCreateActions);
    this.bindActions(LibraryActions);
    this.registerAsync(LibrarySource);
  }

  onResetCreateForm() {
    this.setState(State());
  }

  onChangeField(data) {
    const id = data[0];
    const type = data[1];
    const new_value = data[2];
    const accessor = data[3];

    // un/settings admin permission, should un/set the rest too
    const updateAll = accessor === 'admin_permission';

    const shares = this.state.get('shares').slice(0);

    shares.forEach(function (value, i) {
      if (value.id === id && value.type === type) {
        if (updateAll) {
          value['admin_permission'] = new_value;

          value['create_tags_permission'] = new_value;
          value['add_permission'] = new_value;
          value['remove_permission'] = new_value;

          value['create_tags_permission_disabled'] = new_value;
          value['add_permission_disabled'] = new_value;
          value['remove_permission_disabled'] = new_value;
        } else {
          value[accessor] = new_value;
        }
        shares[i] = value;
      }
    });

    this.setState(this.state.set('shares', shares).set('hasUnsavedShares', true));
  }

  onChangeName(name) {
    this.setState(this.state.set('name', name));
  }

  onChangePurpose(purpose) {
    this.setState(this.state.set('purpose', purpose));
  }

  onChangeShareElementType(type) {
    let state = this.state.set('shareType', type);
    if (type !== this.state.get('shareType')) {
      state = state.set('shareId', null);
    }
    this.setState(state);
  }

  onChangeShareElementId(id) {
    this.setState(this.state.set('shareId', id));
  }

  onAddCurrentSharedElement(element) {
    const shares = this.state.get('shares').slice(0);

    shares.push(element);
    this.setState(this.state.set('shares', shares).set('hasUnsavedShares', true));
  }

  onRemoveTag(removeTag) {
    const tags = this.state.get('tags').filter(tag => {
      return tag !== removeTag;
    });
    this.setState(this.state.set('tags', tags));
  }

  onClearTags() {
    let state = this.state.set('tags', []);
    this.setState(state);
  }

  onClearCurrentChosenTag() {
    let state = this.state.set('currentTag', null);
    this.setState(state);
  }

  onUpdateLibrarySuccess() {
    this.onClearTags();
  }

  onCreateLibrarySuccess() {
    this.onClearTags();
  }

  onSubmit(args) {
    const {user, isUpdateForm, showSharingAndTagSections, adminManage = false} = args;
    const data = {
      id: this.state.get('id'),
      name: this.state.get('name'),
      purpose: this.state.get('purpose'),
    };
    if (showSharingAndTagSections) {
      // Populate any shares / tags iff showSharingAndTagSections is `true`
      data.shares = this.state.get('shares');
      data.tags = this.state.get('tags');
    } else {
      data.modify_tags = false;
    }
    if (data.id) {
      setImmediate(() => LibraryActions.updateLibrary(data, adminManage));
    } else {
      if (!isUpdateForm) {
        let foundUserShare = false;
        if (data.shares) {
          data.shares.forEach(share => {
            if (share.type === 'user' && share.id === user.id) {
              foundUserShare = true;
            }
          });
        }
        if (!foundUserShare) {
          // Populate data.shares with the `user` info since this is a creation of a library
          const element = {
            type: 'user',
            id: user.id,
            name: user.display_name,
            admin_permission: true,
            create_tags_permission: true,
            add_permission: true,
            remove_permission: true,
            view_permission: true,
          };
          if (!data.shares) {
            data.shares = [element];
          } else {
            data.shares.push(element);
          }
        }
      }
      setImmediate(() => LibraryActions.createLibrary(data));
    }

    this.onClearUnsavedShare();
  }

  onClearUnsavedShare() {
    const state = this.state
      .set('shareType', null)
      .set('shareId', null)
      .set('hasUnsavedShares', false);
    this.setState(state);
  }

  onRemoveShare(data) {
    const id = data[0];
    const type = data[1];

    const shares = this.state.get('shares').slice(0);
    const newShares = shares.filter(item => item.id !== id || item.type !== type);

    this.setState(this.state.set('shares', newShares).set('hasUnsavedShares', true));
  }

  onAddCurrentTag(isUpdate) {
    const tags = this.state.get('tags');
    const tag = this.state.get('currentTag');
    if (tags.indexOf(tag) > -1) {
      return null;
    } else if (tag && tag.length > 0) {
      this._addTag(tag, tags);
    }
  }

  _addTag(tag, tags) {
    let tagList = Set(tags);
    tagList = tagList.add(tag);
    let state = this.state.set('tags', tagList.toArray());
    state = state.set('currentTag', null);
    this.setState(state);
  }

  onChangeAndAddCurrentTag(tag) {
    this._addTag(tag, this.state.get('tags'));
  }

  onEditLibrary(libraryId) {
    setImmediate(() => this.getInstance().getLibrary(libraryId, 'editLibrary'));
  }

  onGetLibrarySuccess(argsAndResponse) {
    const {args, response: resp} = argsAndResponse;
    const data = resp.data;
    const library = data.library;
    let state = this.state
      .set('id', library.id)
      .set('name', library.name)
      .set('purpose', library.purpose);

    if (args[1] === 'editLibrary') {
      state = state.set('tags', data.tags);
    }

    // set the permissions
    const shares = data.shares.map(share => {
      if (share.admin_permission) {
        share['create_tags_permission_disabled'] = true;
        share['add_permission_disabled'] = true;
        share['remove_permission_disabled'] = true;
      }
      return share;
    });
    state = state.set('shares', shares).set('hasUnsavedShares', false);
    state = state.set('initialState', state);

    this.setState(state);
  }

  onDeleteLibrary(adminManage) {
    const libraryId = this.state.get('id');
    setImmediate(() => this.getInstance().deleteLibrary(libraryId, adminManage));
  }
}

export default alt.createStore(LibraryCreateFormStore, 'LibraryCreateFormStore');
